import { HelperText } from '~/components/helper-text'
import {
  InputCreditCardActions,
  InputCreditCardButton,
  InputCreditCardField,
  InputCreditCardFlags,
  InputCreditCardHelperText,
  InputCreditCardLabel,
} from './components'
import { InputCreditCardProvider } from './contexts/input-credit-card-context'
import type { IInputCreditCardProps } from './types'

import { Children, isValidElement } from 'react'
import { isDEV, logger } from '~/utils'
import { useInputCreditCardProps } from './hooks'
import * as Comp from './input-credit-card.styles'
import { SCOPE_WARNING_MESSAGE } from './utils/const'

const InputCreditCardRoot = ({
  children,
  size = 'md',
  full,
  disabled = false,
  readonly = false,
  hasError = false,
  errorText = 'Dados inválidos',
  value = '',
  onChangeText,
  testID = 'inputTest',
  ...props
}: IInputCreditCardProps) => {
  const { isActive, setIsActive } = useInputCreditCardProps()

  const renderInternalComponents = Children.map(children, child => {
    if (!isValidElement(child)) return

    if (
      child.type === InputCreditCardLabel ||
      child.type === InputCreditCardActions ||
      child.type === InputCreditCardHelperText
    ) {
      return child
    }

    if (isDEV()) {
      logger.warning(SCOPE_WARNING_MESSAGE)
      return
    }
  })

  const componentProperties = {
    size,
    full,
    hasError,
    errorText,
    disabled,
    readonly,
    value,
    onChangeText,
    isActive,
    setIsActive,
    testID,
  } as const

  return (
    <InputCreditCardProvider {...componentProperties}>
      <Comp.Root {...props} testID={testID}>
        {renderInternalComponents}
        {hasError && (
          <HelperText
            disabled={disabled}
            readonly={readonly}
            hasError={hasError}
            withIcon={true}
            iconName={'rdicon-warning-circle'}
            iconSize="tiny"
          >
            {errorText ?? 'Dados inválidos'}
          </HelperText>
        )}
      </Comp.Root>
    </InputCreditCardProvider>
  )
}

export const InputCreditCard = {
  Root: InputCreditCardRoot,
  Field: InputCreditCardField,
  Label: InputCreditCardLabel,
  Actions: InputCreditCardActions,
  Button: InputCreditCardButton,
  HelperText: InputCreditCardHelperText,
  Flags: InputCreditCardFlags,
}

export type { IInputCreditCardProps }
